import { useEffect, useMemo, useState } from 'react';
import { Maximize2, Search, Shuffle } from 'lucide-react';
import {
  PROTOTYPE_DESIGNS,
  PROTOTYPE_DESIGN_CATEGORIES,
  type PrototypeDesign,
  type PrototypeDesignCategory,
} from '@aiai/shared';
import { Modal } from './Modal';
import { DesignPreviewOverlay } from './DesignPreviewApp';
import { FONT_STACKS, contrastText } from './designTokens';

/**
 * A live swatch card rendered purely from the design's own preview tokens —
 * background, surface, ink, accent, font vibe, radius, border, shadow — so
 * every card in the gallery literally looks like its style. No images, no
 * font loading.
 */
export function DesignSwatch({
  design,
  compact = false,
}: {
  design: PrototypeDesign;
  compact?: boolean;
}) {
  const p = design.preview;
  const fontWeight = p.font === 'display' ? 800 : p.font === 'serif' ? 600 : 700;
  return (
    <div
      className={compact ? 'p-2' : 'p-3'}
      style={{ background: p.bg }}
      aria-hidden="true"
    >
      <div
        className={compact ? 'px-2 py-1.5' : 'px-2.5 py-2'}
        style={{
          background: p.surface,
          color: p.text,
          borderRadius: p.radius,
          border: p.border,
          boxShadow: p.shadow,
        }}
      >
        <div
          className="flex items-baseline justify-between gap-2"
          style={{
            fontFamily: FONT_STACKS[p.font],
            fontWeight,
            letterSpacing: p.font === 'display' ? '-0.02em' : undefined,
          }}
        >
          <span className={compact ? 'text-sm leading-none' : 'text-lg leading-none'}>Aa</span>
          <span
            className={
              compact
                ? 'px-1.5 py-0.5 text-[8px] font-semibold leading-none'
                : 'px-2 py-1 text-[9px] font-semibold leading-none'
            }
            style={{
              background: p.accent,
              color: contrastText(p.accent),
              borderRadius: Math.min(p.radius, 999),
              border: p.border,
            }}
          >
            Go
          </span>
        </div>
        {!compact && (
          <div className="mt-2 space-y-1">
            <div className="h-1 w-3/4 rounded-full" style={{ background: p.text, opacity: 0.75 }} />
            <div className="h-1 w-1/2 rounded-full" style={{ background: p.text, opacity: 0.3 }} />
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Full-catalog gallery: category chips, search, surprise-me, and one live
 * swatch card per design. Clicking a card only highlights it — nothing is
 * committed until the confirm button (bottom right), so browsing is safe even
 * when confirming has side effects (the restyle path files a ticket).
 */
export function DesignPicker({
  open,
  onClose,
  value,
  onSelect,
  allowNone = true,
  confirmLabel = 'Use this style',
}: {
  open: boolean;
  onClose: () => void;
  /** Currently selected design id (null = no preference / default look). */
  value: string | null;
  onSelect: (designId: string | null) => void;
  /** Offer the "No preference" card (the agent picks a style that fits the brief). */
  allowNone?: boolean;
  /** Confirm-button label — say what confirming does (e.g. "File restyle ticket"). */
  confirmLabel?: string;
}) {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<PrototypeDesignCategory | 'all'>('all');
  const [pending, setPending] = useState<string | null>(value);
  /** Design shown in the full-page preview overlay (null = closed). */
  const [previewId, setPreviewId] = useState<string | null>(null);

  // Re-sync the highlight with the committed value each time the picker opens,
  // so an abandoned browse never leaks into the next session.
  useEffect(() => {
    if (open) setPending(value);
    else setPreviewId(null);
  }, [open, value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return PROTOTYPE_DESIGNS.filter(
      (d) =>
        (category === 'all' || d.category === category) &&
        (!q ||
          d.name.toLowerCase().includes(q) ||
          d.tagline.toLowerCase().includes(q) ||
          d.category.includes(q)),
    );
  }, [query, category]);

  const surprise = () => {
    const pool = filtered.length > 0 ? filtered : PROTOTYPE_DESIGNS;
    setPending(pool[Math.floor(Math.random() * pool.length)].id);
  };

  const pendingDesign = pending ? PROTOTYPE_DESIGNS.find((d) => d.id === pending) : undefined;
  const dirty = pending !== value;
  const canConfirm = dirty && (pending !== null || allowNone);

  const confirm = () => {
    onSelect(pending);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Choose a design style"
      widthClass="max-w-4xl"
      footer={
        <>
          <span className="mr-auto self-center truncate text-xs text-ink-faint">
            {pendingDesign
              ? `Selected: ${pendingDesign.name}`
              : pending === null && allowNone
                ? 'Selected: No preference'
                : ''}
          </span>
          {pendingDesign && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setPreviewId(pendingDesign.id)}
            >
              <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
              Preview full page
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={confirm} disabled={!canConfirm}>
            {confirmLabel}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-40 flex-1">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-faint"
              aria-hidden="true"
            />
            <input
              className="input pl-8"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${PROTOTYPE_DESIGNS.length} styles…`}
              aria-label="Search design styles"
            />
          </div>
          <button type="button" className="btn-secondary" onClick={surprise}>
            <Shuffle className="h-3.5 w-3.5" aria-hidden="true" />
            Surprise me
          </button>
        </div>

        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Filter by category">
          {(
            [
              ['all', 'All'],
              ...Object.entries(PROTOTYPE_DESIGN_CATEGORIES),
            ] as Array<[PrototypeDesignCategory | 'all', string]>
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setCategory(key)}
              aria-pressed={category === key}
              className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                category === key
                  ? 'border-ink bg-ink text-surface-1'
                  : 'border-line text-ink-muted hover:border-ink-faint hover:text-ink'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="grid max-h-[55vh] grid-cols-2 gap-2.5 overflow-y-auto pr-1 sm:grid-cols-3 lg:grid-cols-4">
          {allowNone && category === 'all' && !query.trim() && (
            <button
              type="button"
              onClick={() => setPending(null)}
              aria-pressed={pending === null}
              className={`overflow-hidden rounded-[8px] border text-left transition-shadow hover:shadow-md ${
                pending === null ? 'border-signal ring-2 ring-signal/40' : 'border-line'
              }`}
            >
              <div className="p-3" style={{ background: '#f8fafc' }} aria-hidden="true">
                <div
                  className="rounded-lg border border-dashed px-2.5 py-2 text-lg font-semibold"
                  style={{ borderColor: '#cbd5e1', color: '#334155', background: '#ffffff' }}
                >
                  Aa
                </div>
              </div>
              <div className="border-t border-line p-2">
                <p className="text-xs font-semibold text-ink">No preference</p>
                <p className="truncate text-[11px] text-ink-faint">
                  The agent picks a style that fits your brief.
                </p>
              </div>
            </button>
          )}
          {filtered.map((d) => (
            <div key={d.id} className="group relative">
              <button
                type="button"
                onClick={() => setPending(d.id)}
                title={d.tagline}
                aria-pressed={pending === d.id}
                className={`w-full overflow-hidden rounded-[8px] border text-left transition-shadow hover:shadow-md ${
                  pending === d.id ? 'border-signal ring-2 ring-signal/40' : 'border-line'
                }`}
              >
                <DesignSwatch design={d} />
                <div className="border-t border-line p-2">
                  <p className="truncate text-xs font-semibold text-ink">{d.name}</p>
                  <p className="truncate text-[11px] text-ink-faint">{d.tagline}</p>
                </div>
              </button>
              {/* Sibling, not nested: a button inside a button is invalid HTML. */}
              <button
                type="button"
                onClick={() => setPreviewId(d.id)}
                aria-label={`Preview ${d.name} full page`}
                title="Preview full page"
                className="absolute right-1.5 top-1.5 rounded-[5px] border border-line bg-surface-1/90 p-1 text-ink-muted opacity-0 shadow-sm transition-opacity hover:text-ink focus:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60 group-hover:opacity-100"
              >
                <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="col-span-full py-8 text-center text-sm text-ink-faint">
              No styles match “{query.trim()}”.
            </p>
          )}
        </div>
      </div>

      {previewId && (
        <DesignPreviewOverlay
          designs={filtered.some((d) => d.id === previewId) ? filtered : PROTOTYPE_DESIGNS}
          designId={previewId}
          onNavigate={setPreviewId}
          onClose={() => setPreviewId(null)}
          selectedId={pending}
          onChoose={setPending}
        />
      )}
    </Modal>
  );
}
